/**
 * **Panel CRUD master** — daftar + laci formulir, pola yang sama dengan konsol OSB Nexus
 * (`components/crud/CrudPage.tsx` di `~/osbnexus`).
 *
 * ### ⭐ Kenapa bentuknya ditiru, bukan dikarang ulang
 * Imron memakai konsol Nexus tiap hari. Bentuk yang sudah ia hafal — judul, tombol **+ Baru** di
 * kanan atas, tabel yang barisnya bisa diklik, laci yang muncul dari kanan — tak perlu dipelajari
 * dua kali. Layar master OSB POS sebelumnya memakai bentuk lain (daftar sempit + formulir sebaris
 * di bawahnya): ia hemat tempat, tapi **tak punya jalan menyunting** dan menyembunyikan status.
 *
 * ### ⛔ Yang SENGAJA berbeda dari Nexus
 * - **Tanpa react-query.** Portal ini belum memakainya; menambah pustaka cache demi satu layar
 *   berarti dua model pemuatan data hidup berdampingan. Muat ulang di sini eksplisit (`reload`).
 * - **Cari disaring di klien.** Master menu satu brand berukuran ratusan baris, bukan puluhan ribu;
 *   pencarian server menambah endpoint yang belum dibutuhkan siapa pun.
 * - **`blockedReason`** — Nexus tak punya ini. Di sini hierarki MEMAKSA: kategori tak boleh lahir
 *   sebelum departemennya ada, dan sebabnya harus terbaca di layar, bukan ditemukan sebagai galat
 *   server sesudah formulirnya diisi.
 */
import { PlusOutlined } from '@ant-design/icons';
import {
  Button, Collapse, Drawer, Empty, Form, Input, Space, Table, Tooltip, Typography, message,
} from 'antd';
import type { FormInstance } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useCallback, useEffect, useState, type ReactNode } from 'react';

import { MasterHistory } from './MasterHistory';
import { useTranslation } from 'react-i18next';

export interface CrudPanelProps<T extends { id: string }> {
  readonly title: string;
  readonly subtitle?: ReactNode;
  /** Pengambil baris. ⚠️ Bungkus dengan `useCallback` — ia jadi dependensi pemuatan. */
  readonly fetchList: () => Promise<readonly T[]>;
  readonly columns: ColumnsType<T>;
  readonly renderFields: (form: FormInstance, editing: T | null) => ReactNode;
  /** Record → nilai awal formulir saat menyunting. Bawaan: record apa adanya. */
  readonly toFormValues?: (r: T) => Record<string, unknown>;
  /**
   * ⭐ Nilai awal laci **BUAT BARU**.
   *
   * ⛔ Tanpa ini, `resetFields()` meninggalkan tiap `InputNumber` KOSONG — dan formulir yang
   * mengirim field numerik kosong menghasilkan `Number(undefined)` = `NaN`, yang `JSON.stringify`
   * ubah jadi `null`, yang ditolak server sebagai "Expected number, received null". Galat itu
   * menunjuk ke server padahal sebabnya di layar.
   *
   * ⚠️ Bukan sekadar penambal galat: setelan bawaan yang TERLIHAT saat membuat berarti orang yang
   * membuatnya tahu angka apa yang sedang ia setujui. Bawaan yang tak terlihat adalah keputusan
   * yang tak pernah diambil siapa pun.
   */
  readonly createDefaults?: Record<string, unknown>;
  readonly onCreate?: (v: Record<string, unknown>) => Promise<unknown>;
  readonly onUpdate?: (id: string, v: Record<string, unknown>, editing: T) => Promise<unknown>;
  /** ⛔ Sesi hanya-lihat: seluruh jalan tulis tak dipasang, bukan dipasang lalu ditolak. */
  readonly canWrite: boolean;
  readonly newLabel: string;
  readonly formWidth?: number;
  /** Prefiks `data-testid` — baris jadi `<testId>-<code>` lewat kolomnya sendiri. */
  readonly testId: string;
  /**
   * ⭐ Path master untuk **riwayat perubahan** (mis. `products`). Tanpa ini panelnya tak muncul.
   *
   * ⚠️ Sengaja BUKAN diturunkan dari `testId`: keduanya kebetulan mirip pada beberapa layar dan
   * berbeda jauh pada yang lain (`prod` vs `products`, `taxkode` vs `tax-codes`). Menebaknya berarti
   * memanggil endpoint yang tak ada, lalu panelnya diam tanpa sebab yang terlihat.
   */
  readonly historyPath?: string;
  /** Terisi ⇒ membuat baru DIBLOKIR, dan kalimat ini yang menjelaskan sebabnya. */
  readonly blockedReason?: string | null;
  /** Teks yang dicari saat mengetik di kotak cari. Kosong ⇒ kotak cari tak dipasang. */
  readonly searchIn?: (r: T) => string;
  /** ⚠️ `ReactNode`, bukan `string`: layar boleh menaruh `data-testid` di kalimat kosongnya. */
  readonly emptyText?: ReactNode;
  /**
   * ⚠️ Terisi ⇒ klik baris **MEMILIH**, bukan membuka laci.
   *
   * Dipakai entitas yang punya lebih banyak isi daripada yang muat di satu laci — produk membawa
   * flag kontrol, ketersediaan berjadwal, barcode, dan riwayat harga. Menjejalkannya ke laci
   * sempit membuat orang menggulir mencari satu sakelar; menaruhnya di bawah tabel membuatnya
   * terbaca sekaligus. ⛔ Satu baris tak boleh punya DUA arti klik.
   */
  readonly onSelect?: (r: T) => void;
  readonly selectedId?: string | null;
  /**
   * Naikkan angkanya untuk memaksa muat ulang dari luar — dipakai saat kartu detail di bawah
   * tabel menyimpan sesuatu yang ikut tampil di kolom tabel (nama produk, misalnya).
   *
   * ⚠️ Tanpa ini, layar menunjukkan nama lama sesudah nama itu benar-benar berubah — dan yang
   * melihatnya akan menyimpan lagi, mengira simpanan pertamanya gagal.
   */
  readonly reloadToken?: number;
}

export function CrudPanel<T extends { id: string }>(p: CrudPanelProps<T>) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<readonly T[]>([]);
  const [loading, setLoading] = useState(false);
  const [cari, setCari] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<T | null>(null);
  const [busy, setBusy] = useState(false);
  const [form] = Form.useForm();

  const { fetchList } = p;
  const reload = useCallback(() => {
    setLoading(true);
    fetchList()
      .then(setRows)
      .catch((e: Error) => { void message.error(e.message); })
      .finally(() => { setLoading(false); });
  }, [fetchList]);
  const { reloadToken } = p;
  useEffect(() => { reload(); }, [reload, reloadToken]);

  const bukaBaru = () => {
    setEditing(null);
    form.resetFields();
    if (p.createDefaults !== undefined) form.setFieldsValue(p.createDefaults);
    setOpen(true);
  };
  const bukaSuntingPaksa = (r: T) => {
    if (!p.canWrite || p.onUpdate === undefined) return;
    setEditing(r);
    form.resetFields();
    form.setFieldsValue(p.toFormValues === undefined ? r : p.toFormValues(r));
    setOpen(true);
  };
  /** Klik baris: hanya membuka laci bila baris memang TIDAK dipakai memilih. */
  const bukaSunting = (r: T) => {
    if (p.onSelect !== undefined) return;
    bukaSuntingPaksa(r);
  };

  const simpan = async () => {
    const v = (await form.validateFields()) as Record<string, unknown>;
    setBusy(true);
    try {
      if (editing === null) await p.onCreate?.(v);
      else await p.onUpdate?.(editing.id, v, editing);
      void message.success(t('crud.saved'));
      setOpen(false);
      reload();
    } catch (e) {
      void message.error(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const terpakai =
    p.searchIn === undefined || cari.trim() === ''
      ? rows
      : rows.filter((r) => p.searchIn?.(r).toLowerCase().includes(cari.trim().toLowerCase()));

  const bolehBaru = p.canWrite && p.onCreate !== undefined;
  const terkunci = p.blockedReason != null && p.blockedReason !== '';

  return (
    <div data-testid={p.testId}>
      <div className="crud-head">
        <div>
          <Typography.Title level={4} style={{ margin: 0 }}>{p.title}</Typography.Title>
          {p.subtitle !== undefined && (
            <Typography.Text type="secondary">{p.subtitle}</Typography.Text>
          )}
        </div>
        <div style={{ flex: 1 }} />
        {p.searchIn !== undefined && (
          <Input.Search
            allowClear
            placeholder={t('crud.search')}
            style={{ width: 240 }}
            data-testid={`${p.testId}-cari`}
            onChange={(e) => { setCari(e.target.value); }}
          />
        )}
        {bolehBaru && (
          /* ⚠️ Tombolnya TETAP TERLIHAT saat terkunci, hanya mati — supaya "kenapa tak bisa"
             terjawab oleh tooltipnya, bukan oleh tombol yang menghilang tanpa jejak. */
          <Tooltip title={terkunci ? p.blockedReason : undefined}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              disabled={terkunci}
              data-testid={`${p.testId}-baru`}
              onClick={bukaBaru}
            >
              {p.newLabel}
            </Button>
          </Tooltip>
        )}
      </div>

      <Table<T>
        size="middle"
        rowKey="id"
        loading={loading}
        /*
         * ⛔ Saat baris dipakai MEMILIH, menyunting butuh sasaran KLIK SENDIRI — bukan arti kedua
         * pada baris yang sama. Tanpa kolom ini, entitas ber-`onSelect` tak punya jalan sunting
         * sama sekali: persis lubang yang sudah kita tambal di departemen, produk, dan promo.
         */
        columns={
          p.onSelect !== undefined && p.canWrite && p.onUpdate !== undefined
            ? [
                ...p.columns,
                {
                  title: '',
                  key: '__sunting',
                  width: 90,
                  render: (_: unknown, r: T) => (
                    <Button
                      size="small"
                      data-testid={`${p.testId}-sunting-${r.id}`}
                      onClick={(e) => { e.stopPropagation(); bukaSuntingPaksa(r); }}
                    >
                      {t('crud.edit')}
                    </Button>
                  ),
                },
              ]
            : p.columns
        }
        dataSource={[...terpakai]}
        locale={{ emptyText: <Empty description={p.emptyText ?? t('crud.empty')} /> }}
        pagination={terpakai.length > 20 ? { defaultPageSize: 20, showSizeChanger: true } : false}
        rowClassName={(r) => (p.selectedId != null && p.selectedId === r.id ? 'baris-pilih' : '')}
        {...(p.onSelect !== undefined || (p.canWrite && p.onUpdate !== undefined)
          ? {
              onRow: (r: T) => ({
                onClick: () => {
                  if (p.onSelect !== undefined) p.onSelect(r);
                  else bukaSunting(r);
                },
                style: { cursor: 'pointer' },
              }),
            }
          : {})}
      />

      <Drawer
        title={editing === null ? p.newLabel : `${t('crud.edit')} · ${p.title}`}
        width={p.formWidth ?? 460}
        open={open}
        destroyOnHidden
        onClose={() => { setOpen(false); }}
        data-testid={`${p.testId}-laci`}
        extra={
          <Space>
            <Button data-testid={`${p.testId}-batal`} onClick={() => { setOpen(false); }}>
              {t('crud.cancel')}
            </Button>
            <Button
              type="primary"
              loading={busy}
              data-testid={`${p.testId}-simpan`}
              onClick={() => { void simpan(); }}
            >
              {t('crud.save')}
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical" onFinish={() => { void simpan(); }}>
          {p.renderFields(form, editing)}
        </Form>

        {/*
          ⭐⭐ **Riwayat perubahan** — di kaki laci sunting, di bawah formnya.
          ⛔ DI LUAR `<Form>`: ia bukan isian, dan menaruhnya di dalam membuat `Enter` di tabelnya
          ikut men-submit form — satu ketukan tak sengaja menyimpan perubahan yang belum selesai.
          ⚠️ Hanya saat MENYUNTING: baris yang belum ada tak punya riwayat, dan panel kosong di
          form "baru" cuma mengajari orang bahwa panel itu memang selalu kosong.
        */}
        {p.historyPath !== undefined && editing !== null && (
          <Collapse
            ghost
            size="small"
            style={{ marginTop: 8 }}
            data-testid={`${p.testId}-riwayat`}
            items={[{
              key: 'riwayat',
              label: t('history.title'),
              children: <MasterHistory path={p.historyPath} id={(editing as { id: string }).id} />,
            }]}
          />
        )}
      </Drawer>
    </div>
  );
}
